import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  MapPin,
  Users,
  Maximize2,
  DollarSign,
  Sparkles,
  Coins,
  Wifi,
  Coffee,
  Snowflake,
  ParkingCircle,
  ConciergeBell,
  Brush,
  Globe,
  Presentation,
  Clock,
  Lock,
  Building2,
  FileText,
  Monitor,
  Utensils,
  Archive,
  Phone,
  PhoneCall,
  Printer,
  Scan,
  Network,
  Camera,
  ShieldCheck,
  Eye,
  Droplets,
  Flame,
  Thermometer,
  Layers,
  Package,
  Tag,
  Mic,
  Droplet,
  ExternalLink,
  Info,
  Flag,
} from "lucide-react";
import type { RankedSpace } from "../types";
import { useAuth } from "@/features/authentication";
import { ReportProblemsModal } from "./ReportProblemsModal";
import { SpaceComplianceDocuments } from "./SpaceComplianceDocuments";
import { ScoreDetailsModal } from "@/features/vision-360/components/score-details-modal";
import { GetPrivateRoomApi } from "@/features/private-room/list/adapters/get-privateroom/get-privateroom";
import type { PrivateOfficeResponse } from "@/features/private-room/list/adapters/get-privateroom/get-privateroom.types";
import Icon from "@/components/ui/icon";
import { handleSelectIconCategory } from "@/features/vision-360/components/get-icons";
import {
  translateSpaceAmenity,
  translateRoomAmenity,
} from "@/features/vision-360/constants/space-amenities-translations";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { formatDate } from "@/utils/format/format-date";

function formatScore(score: number | null | undefined): string {
  if (score == null || !Number.isFinite(score)) return "0";
  if (Number.isInteger(score)) return `${score}`;
  const fixed = score.toFixed(2);
  return fixed.replace(/0+$/, "").replace(/\.$/, "");
}

const FALLBACK_IMAGE = "/images/placeholder.png";

const AMENITY_CONFIG: Record<
  string,
  { label: string; icon: React.FC<{ className?: string }> }
> = {
  wifi: { label: "Wi-Fi", icon: Wifi },
  cafe: { label: "Café", icon: Coffee },
  ac: { label: "Ar Condicionado", icon: Snowflake },
  parking: { label: "Estacionamento", icon: ParkingCircle },
  reception: { label: "Recepção", icon: ConciergeBell },
  cleaning: { label: "Limpeza", icon: Brush },
  "dedicated-internet": { label: "Internet Dedicada", icon: Globe },
  "meeting-room": { label: "Sala de Reunião", icon: Presentation },
  "wi-fi": { label: "Wi-Fi", icon: Wifi },
  "acesso-24-7": { label: "Acesso 24/7", icon: Clock },
  "controle-de-acesso-digital": { label: "Controle de Acesso", icon: Lock },
  acessibilidade: { label: "Acessibilidade", icon: Package },
  "ar-condicionado": { label: "Ar Condicionado", icon: Snowflake },
  "sala-climatizada": { label: "Sala Climatizada", icon: Thermometer },
  "cortina-ou-persiana": { label: "Cortina/Persiana", icon: Layers },
  mobiliado: { label: "Mobiliado", icon: Package },
  "endereco-comercial": { label: "Endereço Comercial", icon: Building2 },
  "endereco-fiscal": { label: "Endereço Fiscal", icon: FileText },
  "escritorio-virtual": { label: "Escritório Virtual", icon: Monitor },
  "cafe-no-espaco": { label: "Café no Espaço", icon: Coffee },
  "cafe-na-sala": { label: "Café na Sala", icon: Coffee },
  copa: { label: "Copa", icon: Utensils },
  "cozinha-equipada": { label: "Cozinha Equipada", icon: Utensils },
  frigobar: { label: "Frigobar", icon: Utensils },
  "armario-locker": { label: "Armário Locker", icon: Archive },
  armario: { label: "Armário", icon: Archive },
  "sala-de-reuniao": { label: "Sala de Reunião", icon: Presentation },
  "cabine-de-call": { label: "Cabine de Call", icon: PhoneCall },
  "quadro-branco": { label: "Quadro Branco", icon: Presentation },
  auditorio: { label: "Auditório", icon: Mic },
  telefone: { label: "Telefone", icon: Phone },
  janela: { label: "Janela", icon: Layers },
  "banheiro-privado": { label: "Banheiro Privado", icon: Droplet },
  "limpeza-da-sala": { label: "Limpeza", icon: Brush },
  "estacionamento-no-local": { label: "Estacionamento", icon: ParkingCircle },
  "estacionamento-proximo": { label: "Estac. Próximo", icon: ParkingCircle },
  recepcionista: { label: "Recepcionista", icon: ConciergeBell },
  "impressao-pb": { label: "Impressão P&B", icon: Printer },
  "impressao-colorida": { label: "Impressão Colorida", icon: Printer },
  impressora: { label: "Impressora", icon: Printer },
  "scanner-digitalizadora": { label: "Scanner", icon: Scan },
  "link-dedicado": { label: "Link Dedicado", icon: Globe },
  "internet-cabeada": { label: "Internet Cabeada", icon: Network },
  "vpn-private-network-rede-privada": { label: "VPN", icon: Lock },
  "cftv-circuito-fechado-de-tv": { label: "CFTV", icon: Camera },
  "seguranca-monitorada-24h": { label: "Segurança 24h", icon: ShieldCheck },
  "vigilancia-nas-areas-comuns-e-estacionamento": {
    label: "Vigilância",
    icon: Eye,
  },
  "sistemas-de-sprinklers": { label: "Sprinklers", icon: Droplets },
  "detectores-de-fumaca": { label: "Det. de Fumaça", icon: Flame },
};

function renderAmenityTags(amenities: string[]) {
  return amenities.map((amenity) => {
    const config = AMENITY_CONFIG[amenity];
    const label = config?.label ?? translateSpaceAmenity(amenity);
    return (
      <span
        key={amenity}
        className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg font-medium"
        style={{ background: "#F0EEEB", color: "#78736D" }}
      >
        {label}
      </span>
    );
  });
}

interface SpaceDetailDrawerProps {
  open: boolean;
  space: RankedSpace | null;
  onClose: () => void;
  hidePrice?: boolean;
  /** AVCB/alvará block with links to the files (PED-14095). On by default;
   * screens shown to people outside Woba (the partner demo, screen-shared
   * with prospects) turn it off. */
  showComplianceDocuments?: boolean;
}

export function SpaceDetailDrawer({
  open,
  space,
  onClose,
  hidePrice,
  showComplianceDocuments = true,
}: SpaceDetailDrawerProps) {
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const { userInfo } = useAuth();
  const [roomDetails, setRoomDetails] = useState<PrivateOfficeResponse | null>(
    null,
  );
  const [roomDetailsLoading, setRoomDetailsLoading] = useState(false);

  useEffect(() => {
    if (!open || !space?.id) {
      setRoomDetails(null);
      return;
    }
    setRoomDetails(null);
    setRoomDetailsLoading(true);
    GetPrivateRoomApi({ id: space.id })
      .then((data) => setRoomDetails(data.private_office))
      .catch((err) => {
        console.error(
          "[SpaceDetailDrawer] Failed to fetch amenities for space id:",
          space.id,
          err,
        );
        setRoomDetails(null);
      })
      .finally(() => setRoomDetailsLoading(false));
  }, [open, space?.id]);

  if (!open || !space) return null;

  const isVipOffice =
    space.source === "vip-office" && space.origin === "integration";
  const displayCredits =
    space.credits != null && space.credits > 0
      ? space.credits
      : isVipOffice && space.monthlyPrice
        ? Math.ceil(space.monthlyPrice / 80)
        : null;

  const handleImageError = (index: number) => {
    setFailedImages((prev) => new Set(prev).add(index));
  };

  type DrawerPhoto = { url: string; tag: string; idx: number };
  const drawerPhotos: DrawerPhoto[] = [
    ...space.images.map((url, idx) => ({ url, tag: "Foto da Sala", idx })),
    ...(space.spacePhotos ?? []).map((url, idx) => ({
      url,
      tag: "Foto do Espaço",
      idx: space.images.length + idx,
    })),
  ];
  const photosToShow =
    drawerPhotos.length > 0
      ? drawerPhotos
      : [{ url: FALLBACK_IMAGE, tag: "", idx: 0 }];

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent
          className="w-[500px] p-0 sm:max-w-[500px]"
          side="right"
          aria-describedby="space-drawer-description"
        >
          <SheetTitle className="sr-only">{space.spaceName}</SheetTitle>
          <SheetDescription id="space-drawer-description" className="sr-only">
            Detalhes do espaco {space.spaceName}
          </SheetDescription>

          <div className="flex flex-col h-[calc(100vh)] overflow-hidden">
            {/* Header: carousel + title always visible */}
            <div className="flex-none px-6 pt-12 pb-0">
              <div className="relative">
                <button
                  type="button"
                  aria-label="Relatar problemas"
                  title="Relatar problemas"
                  onClick={() => setReportOpen(true)}
                  className="absolute top-2 left-2 z-10 flex items-center justify-center w-8 h-8 rounded-full transition-opacity hover:opacity-80"
                  style={{
                    background: "rgba(26,24,20,0.70)",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <Flag className="w-4 h-4" style={{ color: "#FFD900" }} />
                </button>
                <Carousel className="w-full">
                  <CarouselContent>
                    {photosToShow.map((item) => (
                      <CarouselItem key={item.idx}>
                        <div className="relative aspect-video overflow-hidden rounded-md">
                          <img
                            src={
                              failedImages.has(item.idx)
                                ? FALLBACK_IMAGE
                                : item.url
                            }
                            alt={`${space.spaceName} - ${item.tag || "Imagem"}`}
                            className="w-full h-full object-cover"
                            onError={() => handleImageError(item.idx)}
                          />
                          {item.tag && (
                            <div className="absolute bottom-3 left-3">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                                style={{
                                  background: "rgba(26,24,20,0.70)",
                                  color:
                                    item.tag === "Foto do Espaço"
                                      ? "#A8A29E"
                                      : "#FFD900",
                                  backdropFilter: "blur(4px)",
                                }}
                              >
                                {item.tag}
                              </span>
                            </div>
                          )}
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {photosToShow.length > 1 && (
                    <>
                      <CarouselPrevious className="left-4" />
                      <CarouselNext className="right-4" />
                    </>
                  )}
                </Carousel>
              </div>
            </div>

            <div className="flex-none px-6 pt-4 pb-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3
                    className="text-lg font-bold"
                    style={{ color: "#1A1814" }}
                  >
                    {space.roomName}{" "}
                    <span className="text-xs text-gray-500">
                      #{space.searchId}
                    </span>
                  </h3>
                  <p className="text-sm text-gray-500">{space.spaceName}</p>
                </div>

                {!space.addedById && (
                  <div
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold flex-shrink-0"
                    style={
                      space.score >= 80
                        ? {
                            background: "rgba(255,217,0,0.15)",
                            color: "#78520A",
                          }
                        : { background: "#F0EEEB", color: "#78736D" }
                    }
                  >
                    {space.score >= 80 && <Sparkles className="h-3.5 w-3.5" />}
                    {space.detailedScoreAnalysis?.length ||
                    space.scoreDetails ? (
                      <button
                        type="button"
                        onClick={() => setScoreModalOpen(true)}
                        className="underline decoration-dotted underline-offset-1 hover:opacity-80"
                      >
                        {formatScore(space.score)}%
                      </button>
                    ) : (
                      <span>{formatScore(space.score)}%</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {space.isOutdated && space.daysSinceUpdate != null && (
              <div
                className="mx-6 mb-2 flex items-center gap-2 px-3 py-2 rounded-lg"
                style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}
              >
                <Clock
                  className="w-3.5 h-3.5 flex-shrink-0"
                  style={{ color: "#92400E" }}
                />
                <p className="text-xs" style={{ color: "#92400E" }}>
                  {space.origin === "integration"
                    ? "Dados atualizados pela última vez hoje"
                    : `Dados atualizados pela última vez há ${space.daysSinceUpdate} dia${space.daysSinceUpdate !== 1 ? "s" : ""}`}
                </p>
              </div>
            )}

            {/* Tabs */}
            <Tabs
              defaultValue="detalhes"
              className="flex flex-col flex-1 min-h-0"
            >
              <div className="flex-none px-6 pb-2">
                <TabsList className="w-full">
                  <TabsTrigger value="detalhes" className="flex-1">
                    Detalhes
                  </TabsTrigger>
                  <TabsTrigger value="amenidades" className="flex-1">
                    Amenidades
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent
                value="detalhes"
                className="flex-1 overflow-y-auto min-h-0 mt-0"
              >
                {(space.salesPitch ||
                  (space.matchReasons && space.matchReasons.length > 0)) && (
                  <div
                    className="mx-6 mt-2 mb-0 rounded-xl p-4"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(124,58,237,0.06), rgba(255,217,0,0.06))",
                      border: "1px solid rgba(124,58,237,0.15)",
                    }}
                  >
                    {space.salesPitch && (
                      <p
                        className="text-sm leading-relaxed mb-3"
                        style={{ color: "#43403D" }}
                      >
                        {space.salesPitch}
                      </p>
                    )}
                    {space.matchReasons && space.matchReasons.length > 0 && (
                      <ul className="space-y-1">
                        {space.matchReasons.map((reason, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <span
                              className="text-xs"
                              style={{ color: "#43403D" }}
                            >
                              {reason}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div className="px-6 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className="col-span-2 flex items-start gap-2 rounded-lg p-3"
                      style={{ background: "#FDFBF7" }}
                    >
                      <MapPin
                        className="h-4 w-4 mt-0.5 flex-shrink-0"
                        style={{ color: "#78736D" }}
                      />
                      <div>
                        <div
                          className="text-[10px] font-semibold uppercase tracking-wide"
                          style={{ color: "#78736D" }}
                        >
                          Endereco
                        </div>
                        <div
                          className="text-sm font-medium"
                          style={{ color: "#1A1814" }}
                        >
                          {space.address}
                        </div>
                      </div>
                    </div>

                    <div
                      className="flex items-start gap-2 rounded-lg p-3"
                      style={{ background: "#FDFBF7" }}
                    >
                      <Users
                        className="h-4 w-4 mt-0.5 flex-shrink-0"
                        style={{ color: "#78736D" }}
                      />
                      <div>
                        <div
                          className="text-[10px] font-semibold uppercase tracking-wide"
                          style={{ color: "#78736D" }}
                        >
                          Posições
                        </div>
                        <div
                          className="text-sm font-medium"
                          style={{ color: "#1A1814" }}
                        >
                          {space.capacity > 0
                            ? `${space.capacity} posições`
                            : "Sob consulta"}
                        </div>
                      </div>
                    </div>

                    <div
                      className="flex items-start gap-2 rounded-lg p-3"
                      style={{ background: "#FDFBF7" }}
                    >
                      <Maximize2
                        className="h-4 w-4 mt-0.5 flex-shrink-0"
                        style={{ color: "#78736D" }}
                      />
                      <div>
                        <div
                          className="text-[10px] font-semibold uppercase tracking-wide"
                          style={{ color: "#78736D" }}
                        >
                          Tamanho
                        </div>
                        <div
                          className="text-sm font-medium"
                          style={{ color: "#1A1814" }}
                        >
                          {space.sizeM2 != null
                            ? `${space.sizeM2} m²`
                            : "Sob consulta"}
                        </div>
                      </div>
                    </div>

                    {space.availabilityDate && (
                      <div
                        className="flex items-start gap-2 rounded-lg p-3"
                        style={{ background: "#FEF3C7" }}
                      >
                        <Clock
                          className="h-4 w-4 mt-0.5 flex-shrink-0"
                          style={{ color: "#92400E" }}
                        />
                        <div>
                          <div
                            className="text-[10px] font-semibold uppercase tracking-wide"
                            style={{ color: "#92400E" }}
                          >
                            Disponível em
                          </div>
                          <div
                            className="text-sm font-medium"
                            style={{ color: "#1A1814" }}
                          >
                            {formatDate(space.availabilityDate)}
                          </div>
                        </div>
                      </div>
                    )}

                    {!hidePrice && (
                      <div
                        className="flex items-start gap-2 rounded-lg p-3"
                        style={{ background: "#FDFBF7" }}
                      >
                        <DollarSign
                          className="h-4 w-4 mt-0.5 flex-shrink-0"
                          style={{ color: "#78736D" }}
                        />
                        <div>
                          <div
                            className="text-[10px] font-semibold uppercase tracking-wide"
                            style={{ color: "#78736D" }}
                          >
                            Preço mensal
                          </div>
                          <div
                            className="text-sm font-medium"
                            style={{ color: "#1A1814" }}
                          >
                            {space.monthlyPrice
                              ? `${isVipOffice ? "~" : ""}R$ ${space.monthlyPrice.toLocaleString("pt-BR")}`
                              : "Sob consulta"}
                          </div>
                          {isVipOffice && space.monthlyPrice ? (
                            <div
                              className="text-xs font-medium"
                              style={{ color: "#78736D" }}
                            >
                              Preço médio de referência
                              <span style={{ color: "#78736D" }}>
                                {" · "}valor efetivo sob consulta
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}

                    {!hidePrice && (
                      <div
                        className="flex items-start gap-2 rounded-lg p-3"
                        style={{ background: "#FDFBF7" }}
                      >
                        <DollarSign
                          className="h-4 w-4 mt-0.5 flex-shrink-0"
                          style={{ color: "#78736D" }}
                        />
                        <div>
                          <div
                            className="text-[10px] font-semibold uppercase tracking-wide"
                            style={{ color: "#78736D" }}
                          >
                            Preço/posição
                          </div>
                          <div
                            className="text-sm font-medium"
                            style={{ color: "#1A1814" }}
                          >
                            {space.pricePerWorkstation != null &&
                            space.pricePerWorkstation > 0
                              ? `${isVipOffice ? "~" : ""}R$ ${space.pricePerWorkstation.toLocaleString("pt-BR")}`
                              : "Sob consulta"}
                          </div>
                          {isVipOffice && space.pricePerWorkstation ? (
                            <div
                              className="text-xs font-medium"
                              style={{ color: "#78736D" }}
                            >
                              Preço médio de referência
                              <span style={{ color: "#78736D" }}>
                                {" · "}valor efetivo sob consulta
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}

                    {!hidePrice && (
                      <div
                        className="flex items-start gap-2 rounded-lg p-3"
                        style={{ background: "#FDFBF7" }}
                      >
                        <Coins
                          className="h-4 w-4 mt-0.5 flex-shrink-0"
                          style={{ color: "#78736D" }}
                        />
                        <div>
                          <div
                            className="text-[10px] font-semibold uppercase tracking-wide"
                            style={{ color: "#78736D" }}
                          >
                            Créditos
                          </div>
                          <div
                            className="text-sm font-medium"
                            style={{ color: "#1A1814" }}
                          >
                            {displayCredits
                              ? `${isVipOffice ? "~" : ""}${displayCredits.toLocaleString("pt-BR")}`
                              : "Sob consulta"}
                          </div>
                          {isVipOffice && displayCredits ? (
                            <div
                              className="text-xs font-medium"
                              style={{ color: "#78736D" }}
                            >
                              Estimado · preço mensal ÷ 80
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}

                    {(space as unknown as Record<string, unknown>).source ===
                      "vip-office" && (
                      <div
                        className="flex items-start gap-2 rounded-lg p-3"
                        style={{ background: "#FDFBF7" }}
                      >
                        <Building2
                          className="h-4 w-4 mt-0.5 flex-shrink-0"
                          style={{ color: "#78736D" }}
                        />
                        <div className="min-w-0 flex-1">
                          <div
                            className="text-[10px] font-semibold uppercase tracking-wide"
                            style={{ color: "#78736D" }}
                          >
                            Origem
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span
                              className="text-sm font-medium"
                              style={{ color: "#1A1814" }}
                            >
                              VIP Office
                            </span>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className="inline-flex shrink-0 cursor-help"
                                    style={{ color: "#78736D" }}
                                  >
                                    <Info className="h-3.5 w-3.5" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="max-w-[240px] text-xs"
                                >
                                  <p>
                                    Estes dados vêm da integração com o VIP
                                    Office. Preço e tamanho podem não estar
                                    disponíveis nesta origem.
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {showComplianceDocuments && (
                  <SpaceComplianceDocuments
                    spaceId={space.partnerId}
                    roomId={space.id}
                  />
                )}
              </TabsContent>

              <TabsContent
                value="amenidades"
                className="flex-1 overflow-y-auto min-h-0 mt-0"
              >
                {/* Amenidades da Sala */}
                <div className="px-6 pt-4 pb-6">
                  <h4
                    className="text-sm font-semibold mb-3"
                    style={{ color: "#1A1814" }}
                  >
                    Amenidades da Sala
                  </h4>
                  {roomDetailsLoading ? (
                    <p className="text-xs" style={{ color: "#9E9A94" }}>
                      Carregando amenidades da sala...
                    </p>
                  ) : roomDetails?.grouped_amenities &&
                    roomDetails.grouped_amenities.filter(
                      (e) => e.name !== "notIncluded",
                    ).length > 0 ? (
                    <div className="space-y-5">
                      {roomDetails.grouped_amenities
                        .filter((e) => e.name !== "notIncluded")
                        .map((group) => {
                          const { icon, name: catName } =
                            handleSelectIconCategory(group.name);
                          return (
                            <div key={group.name}>
                              <div className="flex items-center gap-1.5 mb-2">
                                <Icon
                                  name={icon}
                                  style={{
                                    width: 13,
                                    height: 13,
                                    color: "#9E9A94",
                                  }}
                                />
                                <span
                                  className="text-xs font-semibold uppercase tracking-wide"
                                  style={{ color: "#78736D" }}
                                >
                                  {catName}
                                </span>
                                <span
                                  className="text-xs"
                                  style={{ color: "#B8B3AD" }}
                                >
                                  ({group.amenities?.length ?? 0})
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {group.amenities?.map((amenity) => (
                                  <span
                                    key={amenity.id}
                                    className="inline-flex text-xs px-2.5 py-1 rounded-md font-medium"
                                    style={{
                                      background: "#F0EEEB",
                                      color: "#4A453F",
                                    }}
                                  >
                                    {amenity.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : space.amenities && space.amenities.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {space.amenities.map((slug) => {
                        const config = AMENITY_CONFIG[slug];
                        const IconComp = config?.icon ?? Tag;
                        const label =
                          config?.label ?? translateRoomAmenity(slug);
                        return (
                          <span
                            key={slug}
                            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg font-medium"
                            style={{ background: "#F0EEEB", color: "#78736D" }}
                          >
                            <IconComp className="w-3.5 h-3.5" />
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-xs" style={{ color: "#9E9A94" }}>
                      Não informado
                    </span>
                  )}
                </div>

                {space.spaceAmenities !== undefined && (
                  <div className="px-6 pb-6">
                    <h4
                      className="text-sm font-semibold mb-3"
                      style={{ color: "#1A1814" }}
                    >
                      Amenidades do Espaço
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {space.spaceAmenities.length > 0 ? (
                        renderAmenityTags(space.spaceAmenities)
                      ) : (
                        <span className="text-xs" style={{ color: "#9E9A94" }}>
                          Não informado
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
            <div
              className="flex-shrink-0 flex items-center gap-2 px-6 py-4 border-t"
              style={{ borderColor: "#E8E6E3", background: "white" }}
            >
              <a
                href={`${import.meta.env.VITE_PORTAL_URL}/workspaces/private-office/details/${space.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full text-sm font-semibold transition-opacity hover:opacity-85"
                style={{ background: "#1A1814", color: "#FFD900" }}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Acessar sala
              </a>
              <a
                href={`https://www.google.com/maps/place/${encodeURIComponent(space.address)}/@${space.lat},${space.lng},17z`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors hover:bg-[#F5F3EF]"
                style={{ borderColor: "#E8E6E3", color: "#43403D" }}
              >
                <MapPin className="w-3.5 h-3.5" />
                Maps
              </a>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      {scoreModalOpen &&
        (space.detailedScoreAnalysis?.length || space.scoreDetails) && (
          <ScoreDetailsModal
            scoreDetails={{
              scoreDetails: space.detailedScoreAnalysis ?? null,
              details: space.scoreDetails ?? null,
            }}
            match={space.score}
            handleClose={() => setScoreModalOpen(false)}
          />
        )}
      <ReportProblemsModal
        open={reportOpen}
        onOpenChange={setReportOpen}
        spaces={[space]}
        reporterEmail={userInfo?.profile?.email ?? null}
      />
    </>
  );
}
